import { Alert, Box, Button, Chip, Link, Paper, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNotification } from 'hooks/useNotification';
import { TenantStatusTypes } from 'TenantModule/constants';
import { InfraRequestState, InfraRequestStateName, Tenant } from 'TenantModule/types';
import { fetchTenantInfraRequest, requestTenantInfra } from 'thunk';
import { extractApiErrorMessage } from 'utils/extractApiErrorMessage';
import { formatRelativeTime } from 'utils/date';

type ChipColor = 'success' | 'warning' | 'info' | 'error' | 'default';

export const infraRequestStateLabels: Record<InfraRequestStateName, string> = {
  dispatched: 'Requested — generating PR',
  request_failed: 'Request failed',
  dispatch_failed: 'Dispatch failed',
  open: 'PR open',
  merged: 'Merged — awaiting apply',
  applied: 'Applied',
  closed: 'PR closed unmerged',
};

const infraRequestStateColors: Record<InfraRequestStateName, ChipColor> = {
  dispatched: 'info',
  request_failed: 'error',
  dispatch_failed: 'error',
  open: 'info',
  merged: 'warning',
  applied: 'success',
  closed: 'default',
};

const checkConclusionColor = (conclusion: string | undefined): ChipColor => {
  switch (conclusion) {
    case 'success':
      return 'success';
    case 'failure':
    case 'timed_out':
    case 'action_required':
      return 'error';
    case 'pending':
    case 'queued':
    case 'in_progress':
      return 'info';
    default:
      return 'default';
  }
};

// The server 409s a request unless the tenant is still waiting on its infra.
const requestableTenantStatuses = new Set<TenantStatusTypes>([
  TenantStatusTypes.AwaitingInfrastructure,
  TenantStatusTypes.CreationFailed,
]);

// A new request is only meaningful once the previous one is dead: an in-flight or
// merged/applied request would be a duplicate stack PR.
const rerequestableStates = new Set<InfraRequestStateName>([
  'closed',
  'request_failed',
  'dispatch_failed',
]);

/** Pinned "Request infra PR" enable rule (RDM-2277 CONTRACT.md). */
export const canRequestInfraPr = ({
  enabled,
  status,
  infraRequest,
}: {
  enabled: boolean;
  status: TenantStatusTypes;
  infraRequest: InfraRequestState | null | undefined;
}) =>
  enabled &&
  requestableTenantStatuses.has(status) &&
  (!infraRequest || rerequestableStates.has(infraRequest.state));

type LoadState = 'loading' | 'loaded' | 'unavailable';

const ExternalLink = ({ href, children }: { href: string; children: React.ReactNode }) => (
  <Link href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </Link>
);

const CheckRow = ({ label, conclusion }: { label: string; conclusion: string | undefined }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
    <Chip
      label={conclusion || 'missing'}
      size="small"
      color={checkConclusionColor(conclusion)}
      sx={{ minWidth: 72 }}
    />
    <Typography component="span" variant="body2">
      {label}
    </Typography>
  </Box>
);

/**
 * Tenant stack PR status (RDM-2277): state, PR link, Tenant Plan / Drift Gate
 * conclusions, apply result, and the "Request infra PR" (re)dispatch button.
 *
 * Refreshes on mount (the GET forces a GitHub refresh server-side). Hidden when the
 * feature is not armed in this environment and the tenant has no request; an
 * unreachable endpoint never breaks the surrounding lifecycle panel.
 */
export const TenantInfrastructureCard = ({ tenant }: { tenant: Tenant }) => {
  const dispatch = useDispatch();
  const { showSuccess, showError } = useNotification();
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [enabled, setEnabled] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const { tenantName, status } = tenant;
  const infraRequest = tenant.infraRequest ?? null;

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    // TODO: Fix with Redux Toolkit configureStore migration
    dispatch(
      fetchTenantInfraRequest({
        tenantName,
        meta: {
          onSuccess: ({ response }) => {
            if (cancelled) return;
            setEnabled(response?.enabled === true);
            setLoadState('loaded');
          },
          onFailure: () => {
            if (cancelled) return;
            // 404 (older backend) / 5xx / network: degrade, never crash. Treat the
            // feature as unarmed — the button must not offer a dispatch we could
            // not confirm is allowed.
            setEnabled(false);
            setLoadState('unavailable');
          },
        },
      }) as any,
    );
    return () => {
      cancelled = true;
    };
  }, [dispatch, tenantName]);

  // Nothing to show until we know the feature is armed, unless the tenant already
  // carries a request (from GET /tenant/:name) worth displaying.
  if (!infraRequest && (loadState !== 'loaded' || !enabled)) return null;

  const requestAllowed = canRequestInfraPr({ enabled, status, infraRequest });

  const onRequest = () => {
    setIsRequesting(true);
    // TODO: Fix with Redux Toolkit configureStore migration
    dispatch(
      requestTenantInfra({
        tenantName,
        // A re-request keeps the operator's original options (connector agents,
        // vanity domain) rather than silently dropping them.
        ...(infraRequest?.services?.length ? { services: infraRequest.services } : {}),
        ...(infraRequest?.vanityDomain ? { vanityDomain: infraRequest.vanityDomain } : {}),
        meta: {
          onSuccess: ({ response }) => {
            setIsRequesting(false);
            // A GitHub dispatch error comes back as a 201 with state dispatch_failed.
            if (response?.state === 'dispatch_failed') {
              const reason = response.error ? `: ${response.error}` : '';
              showError(`Infra PR dispatch failed${reason}`);
            } else {
              showSuccess(`Infra PR requested for ${tenantName}`);
            }
          },
          onFailure: ({ error }) => {
            setIsRequesting(false);
            showError(`Infra PR request failed: ${extractApiErrorMessage(error)}`);
          },
        },
      }) as any,
    );
  };

  const lastRefreshed = formatRelativeTime(infraRequest?.lastRefreshedAt);

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 3 }} data-testid="tenant-infrastructure-card">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle1" component="h4" sx={{ fontWeight: 600 }}>
          Infrastructure
        </Typography>
        {infraRequest && (
          <Chip
            label={infraRequestStateLabels[infraRequest.state] ?? infraRequest.state}
            size="small"
            color={infraRequestStateColors[infraRequest.state] ?? 'default'}
          />
        )}
      </Box>

      {loadState === 'unavailable' && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
            mb: 1,
          }}
        >
          Live infrastructure status is unavailable right now — showing the last known state.
        </Typography>
      )}

      {infraRequest ? (
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            Stack <code>{infraRequest.stack}</code>
            {infraRequest.dryRun ? ' (dry run)' : ''}
          </Typography>
          {infraRequest.prUrl && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Pull request:{' '}
              <ExternalLink href={infraRequest.prUrl}>
                {infraRequest.prNumber ? `#${infraRequest.prNumber}` : 'open on GitHub'}
              </ExternalLink>
            </Typography>
          )}
          {infraRequest.runUrl && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <ExternalLink href={infraRequest.runUrl}>Request workflow run</ExternalLink>
            </Typography>
          )}
          {infraRequest.checks && (
            <Box sx={{ mt: 1 }}>
              <CheckRow label="Tenant Plan" conclusion={infraRequest.checks.tenantPlan} />
              <CheckRow label="Drift Gate" conclusion={infraRequest.checks.driftGate} />
            </Box>
          )}
          {infraRequest.applyResult && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Apply:{' '}
              <Chip
                label={infraRequest.applyResult}
                size="small"
                color={checkConclusionColor(infraRequest.applyResult)}
              />
              {infraRequest.appliedAt ? ` ${formatRelativeTime(infraRequest.appliedAt)}` : ''}
              {infraRequest.applyRunUrl && (
                <>
                  {' · '}
                  <ExternalLink href={infraRequest.applyRunUrl}>apply run</ExternalLink>
                </>
              )}
            </Typography>
          )}
          {infraRequest.error && (
            <Alert severity="error" sx={{ my: 1 }}>
              {infraRequest.error}
            </Alert>
          )}
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
              mt: 0.5,
            }}
          >
            Requested by {infraRequest.requestedBy} {formatRelativeTime(infraRequest.requestedAt)}
            {lastRefreshed ? ` · last refreshed ${lastRefreshed}` : ''}
          </Typography>
        </Box>
      ) : (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          No tenant stack PR has been requested yet.
        </Typography>
      )}

      {enabled && (
        <Button
          variant="outlined"
          onClick={onRequest}
          disabled={!requestAllowed || isRequesting}
          sx={{ mt: 1 }}
        >
          {isRequesting ? 'Requesting...' : 'Request infra PR'}
        </Button>
      )}
    </Paper>
  );
};
